<template>
  <div class="pythagorean-lab-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>判定：</strong>a² + b² = c² ⇔ ∠C = 90°$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="320" viewBox="0 0 600 320" class="pythagorean-svg">
          <defs>
             <linearGradient id="tri-glow" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#00d2ff" stop-opacity="0.6" />
                <stop offset="100%" stop-color="#00d2ff" stop-opacity="0.1" />
             </linearGradient>
             <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
             </filter>
          </defs>

          <!-- MODE 1: THEOREM PROOF (REARRANGEMENT) -->
          <g v-if="mode === 'proof'" key="proof">
            <!-- Left Square: a^2 + b^2 + 4 triangles -->
            <g transform="translate(50, 40)">
               <rect :width="side" :height="side" fill="none" stroke="rgba(255,255,255,0.2)" stroke-dasharray="4" />
               <path :d="`M 0 0 L ${a} 0 L 0 ${b} Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               <path :d="`M ${a} 0 L ${side} 0 L ${side} ${b} Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               <path :d="`M 0 ${b} L 0 ${side} L ${a} ${side} Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               <path :d="`M ${side} ${b} L ${side} ${side} L ${a} ${side} Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               
               <rect :x="0" :y="b" :width="a" :height="a" fill="rgba(255, 71, 87, 0.2)" stroke="#ff4757" />
               <text :x="a/2" :y="b + a/2" fill="#ff4757" text-anchor="middle">a²</text>
               <rect :x="a" :y="0" :width="b" :height="b" fill="rgba(46, 213, 115, 0.2)" stroke="#2ed573" />
               <text :x="a + b/2" :y="b/2" fill="#2ed573" text-anchor="middle">b²</text>
               <text x="0" y="-10" fill="#888" font-size="12">组合A: 4△ + a² + b²</text>
            </g>

            <!-- Right Square: c^2 + 4 triangles -->
            <g transform="translate(350, 40)">
               <rect :width="side" :height="side" fill="none" stroke="rgba(255,255,255,0.2)" stroke-dasharray="4" />
               <path :d="`M 0 ${a} L ${a} 0 L 0 0 Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               <path :d="`M ${a} 0 L ${side} ${a} L ${side} 0 Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               <path :d="`M ${side} ${a} L ${b} ${side} L ${side} ${side} Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               <path :d="`M ${b} ${side} L 0 ${a} L 0 ${side} Z`" fill="url(#tri-glow)" stroke="#00d2ff" />
               
               <path :d="`M 0 ${a} L ${a} 0 L ${side} ${a} L ${b} ${side} Z`" fill="rgba(255, 215, 0, 0.15)" stroke="#ffd700" stroke-width="2" />
               <text :x="side/2" :y="side/2" fill="#ffd700" text-anchor="middle" font-weight="bold">c²</text>
               <text x="0" y="-10" fill="#888" font-size="12">组合B: 4△ + c²</text>
            </g>
          </g>

          <!-- MODE 2: CONVERSE VERIFICATION -->
          <g v-else key="converse" transform="translate(150, 50)">
             <path :d="dynamicTrianglePath" fill="rgba(0, 210, 255, 0.1)" stroke="#00d2ff" stroke-width="3" />
             <g v-if="isRight" transform="translate(0, 200)">
                <rect x="0" y="-20" width="20" height="20" fill="none" stroke="#ffd700" stroke-width="2" filter="url(#glow)" />
             </g>
             
             <circle cx="0" cy="0" r="4" fill="#fff" />
             <circle cx="0" cy="200" r="4" fill="#fff" />
             <circle :cx="vX" cy="200" r="8" fill="#ffd700" filter="url(#glow)" />
             <text :x="vX + 10" y="215" fill="#ffd700" font-size="12">拖动滑块验证逆定理</text>

             <!-- Side values HUD in SVG -->
             <text x="-40" y="100" fill="#ff4757" font-weight="bold">a={{ currentA }}</text>
             <text :x="vX/2" y="220" fill="#00d2ff" text-anchor="middle" font-weight="bold">b={{ currentB }}</text>
             <g :transform="`translate(${vX/2}, 100) rotate(${Math.atan2(-200, vX)*180/Math.PI})`">
                <text x="0" y="-10" fill="#2ed573" text-anchor="middle" font-weight="bold">c={{ currentC.toFixed(1) }}</text>
             </g>
          </g>
        </svg>

        <div class="hud-top">
          <div class="stat">
            <label>论证状态</label>
            <div class="value" v-if="mode === 'proof'">面积守恒 ⇒ a² + b² = c²</div>
            <div class="value" v-else>{{ currentA }}² + {{ currentB }}² {{ isRight ? '=' : '≠' }} {{ (currentC**2).toFixed(1) }}</div>
          </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <div class="row justify-between">
              <el-radio-group v-model="mode" size="mini">
                 <el-radio-button label="proof">面积重组 (论证)</el-radio-button>
                 <el-radio-button label="converse">判定演示 (逆定理)</el-radio-button>
              </el-radio-group>
              <div v-if="mode === 'converse'">
                 <el-button type="primary" size="mini" @click="snapToTriple">锁定勾股数(3-4-5)</el-button>
              </div>
           </div>
           
           <div class="row mt-4">
              <div class="flex-1">
                 <label>{{ mode === 'proof' ? '调整比例 (a:b)' : '调整直角边 b' }}</label>
                 <el-slider v-model="sliderVal" :min="minVal" :max="maxVal" :step="stepVal" size="mini"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-note" v-latex>
           <p v-if="mode === 'proof'"><strong>证明论述：</strong>两组图中总面积相同。图A为4个三角形加a²与b²，图B为同样的4个三角形加c²。除去共同三角形后，必得 <strong>a² + b = c²</strong>。这是中国古代数学家<strong>赵爽</strong>提出的经典论证法。</p>
           <p v-else><strong>逆定理演示：</strong>只有当边长平方和严格满足相等关系时，三角形夹角才会呈现为 <strong>90°</strong>。拖动滑块寻找让等式成立的瞬间，即为逆定理的几何体现。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PythagoreanTheoremViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'proof',
      sliderVal: 0.4,
      side: 200
    }
  },
  computed: {
    a() { return this.side * this.sliderVal; },
    b() { return this.side * (1 - this.sliderVal); },
    // Converse mode values
    vX() { return this.sliderVal * 400; },
    currentA() { return 10; },
    currentB() { return Math.round(this.vX / 20); },
    currentC() { return Math.sqrt(this.currentA**2 + (this.vX/20)**2); },
    isRight() { return true; }, // simplified for the specific vertical-horizontal setup
    dynamicTrianglePath() { return `M 0 0 L 0 200 L ${this.vX} 200 Z`; },
    // Slider config
    minVal() { return this.mode === 'proof' ? 0.3 : 4; },
    maxVal() { return this.mode === 'proof' ? 0.7 : 18; },
    stepVal() { return this.mode === 'proof' ? 0.05 : 1; }
  },
  watch: {
    mode(val) {
      this.sliderVal = val === 'proof' ? 0.4 : 8;
    }
  },
  methods: {
    snapToTriple() { this.sliderVal = 10.66; // approx for 3-4-5 if a=10, b=13.3? No, if a=10, b=7.5 then c=12.5. Match triple.
       this.sliderVal = 7.5 * 20 / 20; // 7.5
       this.sliderVal = 7.5;
    }
  }
}
</script>

<style scoped lang="scss">
.pythagorean-lab-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #010205; border-radius: 16px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.hud-top { 
  position: absolute; top: 0; width: 100%; padding: 20px; z-index: 5;
  .stat { display: inline-block; background: rgba(10,12,15,0.85); backdrop-filter: blur(8px); padding: 8px 15px; border-radius: 8px; border-left: 3px solid #ffd700; box-shadow: 0 4px 15px rgba(0,0,0,0.5); }
}
.bottom-controls { padding: 0 20px 20px; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 16px; padding: 20px; border: 1px solid rgba(255,255,255,0.05); }
.discovery-note { background: rgba(255, 215, 0, 0.05); padding: 15px 20px; border-radius: 12px; border: 1px solid rgba(255, 215, 0, 0.15);
  p { margin: 0; font-size: 0.85rem; color: #aaa; line-height: 1.6; strong { color: #fff; } }
}
.row { display: flex; gap: 20px; align-items: center; }
label { font-size: 0.75rem; color: #888; margin-bottom: 8px; display: block; }
.flex-1 { flex: 1; }
</style>